/**
 * Tipos para exportação/importação de Workspace CRM
 * 
 * Define as interfaces do formato `.uaizyworkspace` usado para
 * transferir estruturas completas de workspace entre tenants.
 * 
 * RELEASE: 1.0.0 - 2026-03-22 - Fase 1: Infraestrutura base
 * 
 * @author Sistema
 * @version 1.0.0
 */

import type { FlowExportFormat } from '@/types/flows';
import type { Json } from '@/integrations/supabase/types';

/**
 * Versão atual do formato de exportação de workspace
 */
export const WORKSPACE_EXPORT_FORMAT_VERSION = '1.0';

/**
 * Módulos disponíveis para exportação/importação
 */
export interface WorkspaceExportModules {
  /** Pipelines e suas fases */
  pipelines: boolean;
  /** Categorias de produto vinculadas ao workspace */
  product_categories: boolean;
  /** Produtos vinculados ao workspace */
  products: boolean;
  /** Tabelas de preço vinculadas ao workspace */
  price_tables: boolean;
  /** Modelos e contratos publicados */
  contracts: boolean;
  /** Tipos de agendamento online */
  scheduling: boolean;
  /** Fluxos que referenciam o workspace */
  flows: boolean;
  /** Kanban de conversas (independente do workspace) */
  conversation_kanban: boolean;
}

/**
 * Pipeline exportado (sem IDs internos)
 */
export interface ExportedPipeline {
  name: string;
  description: string | null;
  is_default: boolean;
  sort_order: number;
  phases: ExportedPhase[];
}

/**
 * Fase de pipeline exportada
 */
export interface ExportedPhase {
  name: string;
  color: string | null;
  sort_order: number;
  is_won: boolean;
  is_lost: boolean;
  probability_percent: number | null;
  auto_move_days: number | null;
  description: string | null;
  icon: string | null;
}

/**
 * Categoria de produto exportada
 */
export interface ExportedProductCategory {
  name: string;
  color: string | null;
  icon: string | null;
  description: string | null;
  sort_order: number;
  /** Nome da categoria pai para resolver hierarquia na importação */
  parent_name: string | null;
}

/**
 * Produto exportado
 */
export interface ExportedProduct {
  name: string;
  sku: string | null;
  description: string | null;
  default_price: number;
  cost_price: number | null;
  currency: string;
  unit_type: string;
  product_type: string;
  /** Nome da categoria para match na importação */
  category_name: string | null;
  custom_fields: Json | null;
}

/**
 * Item de tabela de preço exportado
 */
export interface ExportedPriceTableItem {
  /** Nome do produto para match na importação */
  product_name: string;
  price: number;
  min_quantity: number;
  discount_percent: number | null;
  discount_fixed: number | null;
}

/**
 * Tabela de preço exportada
 */
export interface ExportedPriceTable {
  name: string;
  description: string | null;
  currency: string;
  is_default: boolean;
  conditions: Json | null;
  items: ExportedPriceTableItem[];
}

/**
 * Modelo de contrato exportado
 */
export interface ExportedContract {
  template: {
    name: string;
    description: string | null;
    category: string;
    content_html: string;
    variables: Json;
    allowed_delivery_methods: string[];
    require_selfie: boolean;
    show_page_numbers: boolean | null;
    page_number_position: string | null;
    /** Margem superior do background em mm */
    bg_margin_top_mm?: number;
    /** Margem inferior do background em mm */
    bg_margin_bottom_mm?: number;
    /** Offset Y da paginação em mm */
    pagination_y_offset_mm?: number;
  };
  /** Contrato publicado associado (se houver) */
  published: {
    name: string;
    description: string | null;
    status: string;
    default_signers: Json;
    allowed_delivery_methods: string[];
    require_selfie: boolean;
  } | null;
}

/**
 * Disponibilidade de agendamento exportada
 */
export interface ExportedAvailability {
  day_of_week: number;
  start_time: string;
  end_time: string;
  /** Campo real no banco é `is_available` (não `is_active`) */
  is_available: boolean;
  /** Intervalo entre slots em minutos */
  slot_interval_minutes: number;
}

/**
 * Tipo de evento de agendamento exportado
 */
export interface ExportedSchedulingEventType {
  title: string;
  slug: string;
  description: string | null;
  duration_minutes: number;
  buffer_before_minutes: number;
  buffer_after_minutes: number;
  min_notice_hours: number;
  max_advance_days: number;
  timezone: string;
  color: string | null;
  confirmation_message: string | null;
  custom_questions: Json | null;
  crm_auto_create_deal: boolean;
  crm_source: string | null;
  crm_seller_assignment_mode: string;
  /** Nome do pipeline para match na importação */
  crm_pipeline_name: string | null;
  /** Nome da fase para match na importação */
  crm_phase_name: string | null;
  availability: ExportedAvailability[];
}

/**
 * Fase de Kanban de conversas exportada
 */
export interface ExportedKanbanPhase {
  name: string;
  color: string | null;
  position: number;
  is_default: boolean;
  is_final: boolean;
}

/**
 * Categoria de Kanban de conversas exportada
 */
export interface ExportedKanbanCategory {
  name: string;
  color: string | null;
  description: string | null;
  position: number;
  is_default: boolean;
  phases: ExportedKanbanPhase[];
}

/**
 * Formato completo de exportação de workspace
 * Arquivo .uaizyworkspace (JSON)
 */
export interface WorkspaceExportFormat {
  /** Versão do formato para compatibilidade futura */
  format_version: string;
  /** Data/hora da exportação em ISO 8601 */
  export_date: string;
  /** Aplicação de origem */
  source_app: 'UaizyChat';

  /** Módulos que foram incluídos na exportação */
  exported_modules: WorkspaceExportModules;

  /** Dados do workspace */
  workspace: {
    name: string;
    description: string | null;
    color: string | null;
    icon: string | null;
    /** Slug da categoria (match global na importação) */
    category_slug: string;
    auto_distribution_enabled: boolean;
  };

  /** Pipelines com fases aninhadas */
  pipelines: ExportedPipeline[];
  /** Categorias de produto */
  product_categories: ExportedProductCategory[];
  /** Produtos */
  products: ExportedProduct[];
  /** Tabelas de preço com itens */
  price_tables: ExportedPriceTable[];
  /** Modelos de contrato + publicados */
  contracts: ExportedContract[];
  /** Tipos de evento de agendamento */
  scheduling_event_types: ExportedSchedulingEventType[];
  /** Fluxos no formato existente FlowExportFormat */
  flows: FlowExportFormat[];
  /** Categorias de Kanban de conversas com fases */
  kanban_categories: ExportedKanbanCategory[];
}

/**
 * Contagem de entidades para preview da exportação
 */
export interface WorkspaceExportPreview {
  pipelines: number;
  phases: number;
  product_categories: number;
  products: number;
  price_tables: number;
  contracts: number;
  scheduling_event_types: number;
  flows: number;
  kanban_categories: number;
  kanban_phases: number;
}
